// src/pages/f1-quantilia/F1StrategyMetadata/new.tsx

import { Form } from "@/components/ui/form";
import { useEditForm } from "@/features/Edit/hooks";
import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  CheckboxField,
  InputField,
  SelectField,
} from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import i18n from "@/i18n";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";

// Import select options from the filter file
import { fundEsgRatingOptions, subAssetClassOptions } from "./filter";

// Zod schema for new F1StrategyMetadata
const FormSchema = z.object({
  strategy_id: z.coerce
    .number()
    .min(1, i18n.t("validation.strategy_id_required")),
  commitment_fgao: z.coerce.number().optional().nullable(),
  commitment_fgti: z.coerce.number().optional().nullable(),
  commitment_fgao_rentes: z.coerce.number().optional().nullable(),
  dedicated_fund: z.boolean().optional().nullable(),
  exemption_comment: z.string().nullable().optional(),
  sub_asset_class: z.string().nullable().optional(),
  fund_esg_rating_level: z.coerce.number().optional().nullable(),
});

export function F1StrategyMetadataNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client: Clients.F1_QUANTILIA,
    table_name: Tables.F1_STRATEGY_METADATA,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
  });

  const navigateToList = () => {
    navigate(-1);
  };

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("actions.new")}{" "}
                {t(`${Tables.F1_STRATEGY_METADATA}.title`, {
                  ns: "tables",
                  defaultValue: "F1 Strategy Metadata",
                })}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                {/* General Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="strategy_id"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.strategy_id`,
                      {
                        ns: "tables",
                        defaultValue: "ID Stratégie",
                      },
                    )}
                    type="number"
                    isRequired
                    control={form.control}
                  />
                  <CheckboxField
                    name="dedicated_fund"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.dedicated_fund`,
                      {
                        ns: "tables",
                        defaultValue: "Fonds Dédié",
                      },
                    )}
                    control={form.control}
                  />
                </div>

                {/* Sub Asset Class */}
                <div className="grid grid-cols-1 gap-4">
                  <SelectField
                    name="sub_asset_class"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.sub_asset_class`,
                      {
                        ns: "tables",
                        defaultValue: "Sous-classe d'actifs (immobilier)",
                      },
                    )}
                    options={subAssetClassOptions}
                    control={form.control}
                    allow_empty_option={true}
                  />
                </div>

                {/* Commitments */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField
                    name="commitment_fgao"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.commitment_fgao`,
                      {
                        ns: "tables",
                        defaultValue: "Engagement fgao",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="commitment_fgti"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.commitment_fgti`,
                      {
                        ns: "tables",
                        defaultValue: "Engagement fgti",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="commitment_fgao_rentes"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.commitment_fgao_rentes`,
                      {
                        ns: "tables",
                        defaultValue: "Engagement fgao rentes",
                      },
                    )}
                    type="number"
                    control={form.control}
                  />
                </div>

                {/* ESG Rating */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="fund_esg_rating_level"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.fund_esg_rating_level`,
                      {
                        ns: "tables",
                        defaultValue: "Note ESG fonds",
                      },
                    )}
                    options={fundEsgRatingOptions}
                    control={form.control}
                    allow_empty_option={true}
                  />
                  <InputField
                    name="exemption_comment"
                    label={t(
                      `${Tables.F1_STRATEGY_METADATA}.columns.exemption_comment`,
                      {
                        ns: "tables",
                        defaultValue: "Dérogation",
                      },
                    )}
                    control={form.control}
                  />
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation before creating new item */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields ? NewDiff(changedFields, []) : t("no_changes")}
          </ConfirmationModal>

          {/* Cancel (navigate back) confirmation */}
          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
